/**
 * Dataset utility functions for OpenSearch model mapping and configuration
 */

/**
 * Map a dataset key to the model type the search endpoints accept.
 *
 * The registered catalog is authoritative, so a dataset the backend publishes
 * but this module never hardcoded still routes to its own index. The switch
 * below is only the pre-registration fallback (synchronous callers that run
 * before the catalog fetch resolves), and an unrecognized key degrades to
 * 'Person' rather than 422ing /api/v2/search.
 */
export function getModelTypeFromDataset(dataset: string): string {
  // SQL datasets (RFC 062) use their slug as the model type — the
  // sql-search endpoints are keyed by slug.
  if (isSqlDataset(dataset)) return dataset;

  const registered = openSearchDatasetRegistry.find((d) => d.key === dataset);
  if (registered) return registered.modelType;

  switch (dataset) {
    case 'people':
      return 'Person';
    case 'companies':
      return 'Company';
    case 'tas-program-instances':
      return 'TasProgramInstance';
    // Virtual TAS models: TasProgramInstance index + injected record_type filter.
    case 'tas-applications':
      return 'TasApplication';
    case 'tas-assets':
      return 'TasAsset';
    case 'tas-program-templates':
      return 'TasProgramTemplate';
    case 'tas-event-transitions':
      return 'TasEventTransition';
    case 'pslf-forms':
      return 'PslfForm';
    case 'tas-audit-queues':
      return 'AuditQueue';
    case 'tas-audit-logs':
      return 'AuditLog';
    case 'syf-match-participants':
      return 'MatchParticipant';
    case 'syf-match-payments':
      return 'MatchPayment';
    case 'syf-match-participant-benefit-years':
      return 'MatchParticipantBenefitYear';
    case 'slr-contribution-plan-participants':
      return 'EmployeePlan';
    case 'loan-account-balance-snapshots':
      return 'ObservableEntitySnapshot';
    case 'slr-transactions':
      return 'AccountingTransaction';
    case 'recommendations':
      return 'Recommendation';
    default:
      return 'Person';
  }
}

/**
 * Request model override for reporting search endpoints.
 * Persisted report config can keep the selected dataset model type while
 * outgoing /fields and /search payloads opt into application-level TAS data.
 */
export function getSearchRequestModelType(
  modelType: string,
  showIndividualApplicationsOnly = false
): string {
  if (modelType === 'TasProgramInstance' && showIndividualApplicationsOnly) {
    return 'TasApplication';
  }
  return modelType;
}

/**
 * Get the OpenSearch field name used for company filtering on a given dataset.
 * Returns undefined for datasets where company filtering is not applicable.
 */
export function getCompanyFilterField(dataset: string): string | undefined {
  // SQL datasets expose their tenant column as plain company_id in the
  // declared catalog; the backend additionally force-scopes by the
  // company_id request param regardless of client filters.
  if (isSqlDataset(dataset)) return 'company_id';

  switch (dataset) {
    case 'tas-program-instances':
    case 'tas-applications':
    case 'tas-assets':
      return 'company_id_keyword';
    case 'tas-program-templates':
      return 'tas_program_template_company_id_keyword';
    case 'tas-event-transitions':
      return 'company_id_keyword';
    case 'tas-audit-queues':
    case 'tas-audit-logs':
      return 'company_id_keyword';
    case 'pslf-forms':
      return 'pslf_form_company_id_keyword';
    case 'people':
      return 'company_ids';
    case 'companies':
      return 'company_id_keyword';
    case 'syf-match-participants':
      return 'syf_match_participant_company_id_keyword';
    case 'syf-match-payments':
      return 'syf_match_participant_company_id_keyword';
    case 'syf-match-participant-benefit-years':
      return 'syf_match_participant_benefit_year_company_id_keyword';
    case 'slr-contribution-plan-participants':
      return 'slr_contribution_plan_participant_company_id_keyword';
    // Person → company rollup; matches the backend company_filter_field and
    // the reserved ReportQueryBuilder::DATASET_COMPANY_FIELDS slug.
    case 'loan-account-balance-snapshots':
      return 'company_id_keyword';
    // Derived from the line-item employee's company, not the transaction's own
    // nullable company_id -- matches ReportQueryBuilder::DATASET_COMPANY_FIELDS.
    case 'slr-transactions':
      return 'slr_transaction_company_id_keyword';
    // One recommendation can span a user's multiple employers, so this is an
    // array field (all_company_ids_keyword), not a single company_id -- matches
    // ReportQueryBuilder::DATASET_COMPANY_FIELDS on the backend.
    case 'recommendations':
      return 'all_company_ids_keyword';
    default:
      return undefined;
  }
}

/**
 * Reverse-map an OpenSearch model type back to a dataset key.
 * Returns undefined if no matching dataset is found.
 */
export function getDatasetFromModelType(modelType: string): string | undefined {
  if (isSqlDataset(modelType)) return modelType;

  const entry = openSearchDatasetRegistry.find((d) => d.modelType === modelType);
  return entry?.key;
}

/**
 * Resolve a persisted report-config model type to a value the search
 * endpoints accept. Saved configs sometimes carry a dataset key where a
 * model type belongs, or a SQL-dataset slug that is no longer registered
 * (unpublished/deleted, or the registry fetch failed) — an unknown slug
 * sent to /api/v2/search draws a 422 "Invalid model_type" for every
 * fields/search/facet call the viewer makes. Accept model types, classic
 * dataset keys, and registered SQL slugs; return undefined for anything
 * else so callers can fail fast with a readable error instead.
 */
export function resolveReportModelType(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw) return undefined;
  // Registered SQL-backed dataset (RFC 062): slug doubles as model type.
  if (isSqlDataset(raw)) return raw;
  const entry = openSearchDatasetRegistry.find((d) => d.modelType === raw || d.key === raw);
  if (entry) return entry.modelType;
  return EXTRA_REPORT_MODEL_TYPES.has(raw) ? raw : undefined;
}

// Search-supported model types saved configs can carry outside the OpenSearch/SQL dataset registries — anything else fails fast instead of 422ing /api/v2/search.
const EXTRA_REPORT_MODEL_TYPES = new Set([
  // Individual-applications view of the TAS programs dataset
  // (getSearchRequestModelType with showIndividualApplicationsOnly).
  'TasApplication',
  // Institution typeahead / developer search-index tooling.
  'TasUnifiedInstitution',
]);

export type ProductCode = 'TAS' | 'PSLF' | 'SYF' | 'SLR' | 'STRATEGYFINDER' | 'Core';

// OpenSearch datasets, fetched from GET /api/v2/search/datasets and registered by the host app before the builder renders; starts empty until then.
export type OpenSearchDatasetEntry = {
  key: string;
  label: string;
  modelType: string;
  product: ProductCode;
};

let openSearchDatasetRegistry: OpenSearchDatasetEntry[] = [];

/**
 * Fill the OpenSearch dataset registry from a fetched catalog. Replaces the
 * whole registry rather than merging, so a dataset the caller omits stops
 * being routable; a `product` outside PRODUCT_LABELS is normalized to 'Core'
 * so an unknown product still groups in the pickers instead of vanishing.
 * Must run before registerSqlDatasets — that function's reserved-key guard
 * reads the keys written here.
 */
export function registerOpenSearchDatasets(
  datasets: Array<{ slug: string; label: string; modelType: string; product: string }>
) {
  openSearchDatasetRegistry = datasets.map((dataset) => ({
    key: dataset.slug,
    label: dataset.label,
    modelType: dataset.modelType,
    product: (Object.hasOwn(PRODUCT_LABELS, dataset.product)
      ? dataset.product
      : 'Core') as ProductCode,
  }));
}

/**
 * Registered OpenSearch datasets, as a defensive copy — the registry must only
 * change via registerOpenSearchDatasets, since a consumer sort/push on the live
 * array would corrupt query routing session-wide. Empty until a host route
 * registers the catalog.
 */
export function openSearchDatasets(): OpenSearchDatasetEntry[] {
  return [...openSearchDatasetRegistry];
}

/** Merged accessor for pickers/consumers: OpenSearch datasets + SQL datasets. */
export function allDatasets(): Array<OpenSearchDatasetEntry | SqlDatasetEntry> {
  return [...openSearchDatasets(), ...sqlDatasets()];
}

// SQL-backed datasets (RFC 062), fetched from /api/v2/sql-datasets; must register after OpenSearch datasets so the reserved-key guard below sees real keys.
export type SqlDatasetEntry = {
  key: string;
  label: string;
  modelType: string;
  product: string;
  sourceKind: 'sql';
  crossCompanyCapable: boolean;
};

let sqlDatasetRegistry: SqlDatasetEntry[] = [];

export function registerSqlDatasets(
  datasets: Array<{
    slug: string;
    label: string;
    product?: string | null;
    crossCompanyCapable?: boolean;
  }>
) {
  // Slugs become dataset keys/model types — a collision with a classic
  // dataset key (e.g. 'people') would silently reroute an OpenSearch
  // dataset to sql-search and duplicate picker options. Reserved keys win.
  const reserved = new Set<string>(openSearchDatasetRegistry.map((dataset) => dataset.key));
  sqlDatasetRegistry = datasets
    .filter((dataset) => {
      if (reserved.has(dataset.slug)) {
        console.error(
          `Ignoring SQL dataset with reserved slug '${dataset.slug}' — it collides with a classic dataset key`
        );
        return false;
      }
      return true;
    })
    .map((dataset) => ({
      key: dataset.slug,
      label: dataset.label,
      modelType: dataset.slug,
      product: dataset.product ?? 'Core',
      sourceKind: 'sql' as const,
      crossCompanyCapable: dataset.crossCompanyCapable ?? false,
    }));
}

export function sqlDatasets() {
  // Defensive copy: the registry must only change via registerSqlDatasets —
  // a consumer sort/push on the live array would corrupt routing session-wide.
  return [...sqlDatasetRegistry];
}

export function isSqlDataset(datasetOrModelType: string): boolean {
  return sqlDatasetRegistry.some((entry) => entry.key === datasetOrModelType);
}

export function isCrossCompanyCapable(datasetOrModelType: string): boolean {
  return sqlDatasetRegistry.some(
    (entry) => entry.key === datasetOrModelType && entry.crossCompanyCapable
  );
}

export function getProductForDataset(dataset: string): ProductCode {
  const entry = openSearchDatasetRegistry.find((d) => d.key === dataset);
  if (entry) return entry.product;

  const sqlEntry = sqlDatasetRegistry.find((d) => d.key === dataset);
  if (sqlEntry && Object.hasOwn(PRODUCT_LABELS, sqlEntry.product)) {
    return sqlEntry.product as ProductCode;
  }

  return 'Core';
}

export function getReportTypeForDataset(dataset: string): string {
  return getProductForDataset(dataset).toLowerCase();
}

export const PRODUCT_LABELS: Record<ProductCode, string> = {
  TAS: 'Tuition Assistance',
  PSLF: 'PSLF',
  SYF: 'Secure',
  SLR: 'Loan Repayment',
  STRATEGYFINDER: 'Strategy Finder',
  Core: 'Core',
};

export const PRODUCT_SECTION_ORDER: ProductCode[] = [
  'TAS',
  'PSLF',
  'SLR',
  'SYF',
  'STRATEGYFINDER',
  'Core',
];

export const VALID_REPORT_TYPES = new Set(PRODUCT_SECTION_ORDER.map((code) => code.toLowerCase()));

/**
 * Comma-joined builder report types for the `filter[report_type]` query param
 * on the reports listing. jsonapi-resources turns this into a `WHERE IN`, so
 * the listing only fetches builder-supported products (not legacy
 * chart_builder/table_builder/aggregation_table reports). Derived from
 * VALID_REPORT_TYPES so the product set stays defined in one place.
 */
export const REPORT_TYPE_FILTER_VALUE = [...VALID_REPORT_TYPES].join(',');

/**
 * Legacy builder report_type values that still exist on saved reports
 * (pre-product-code records and API-created copies of the older global
 * widget reports). They open fine in the builder/viewer, so the admin
 * listing fetches them too and derives their product from the saved
 * dataset (getProductForReport) instead of silently dropping them.
 */
export const LEGACY_REPORT_TYPES = ['table_builder', 'chart_builder', 'aggregation_table'];

export const LISTING_REPORT_TYPE_FILTER_VALUE = [
  ...VALID_REPORT_TYPES,
  ...LEGACY_REPORT_TYPES,
].join(',');

/**
 * Resolve the product section for a saved report. A product-code
 * report_type (tas/pslf/slr/syf/core) wins; otherwise the product is
 * derived from the dataset stored in the report's search config, falling
 * back to 'Core' when neither identifies a product.
 */
export function getProductForReport(
  reportType: string | null | undefined,
  searchConfig?: { dataset?: unknown; selectedDataset?: unknown } | null
): ProductCode {
  const raw = (reportType || '').toUpperCase();
  if (Object.hasOwn(PRODUCT_LABELS, raw)) return raw as ProductCode;
  const dataset = searchConfig?.dataset ?? searchConfig?.selectedDataset;
  if (typeof dataset === 'string' && dataset) return getProductForDataset(dataset);
  return 'Core';
}

/**
 * Time range options for filtering chart data
 */
export const TIME_RANGE_OPTIONS = [
  { key: 'all', label: 'All Data' },
  { key: '3m', label: 'Last 3 Months' },
  { key: '6m', label: 'Last 6 Months' },
  { key: '12m', label: 'Last 12 Months' },
  { key: '24m', label: 'Last 24 Months' },
  { key: '60m', label: 'Last 5 Years' },
];

/**
 * Get default sort fields for a dataset.
 * These are the OpenSearch field names (snake_case) used for ordering results.
 *
 * Note: Only use keyword-type fields for sorting. Text fields will cause
 * OpenSearch errors unless fielddata is enabled or a .keyword sub-field exists.
 * Use `_score:desc` when no reliable keyword sort field is available.
 */
export function getDefaultSortFields(dataset: string): string[] {
  switch (dataset) {
    case 'slr-transactions':
      // payment_date is the batch date and the most trustworthy ordering key;
      // transaction_date approximates created_at on non-migrated rows.
      return ['slr_transaction_payment_date_date:desc'];
    case 'tas-program-instances':
      return ['tas_program_instance_created_at_date:desc', 'tas_application_created_at_date:desc'];
    case 'tas-applications':
      return ['tas_application_created_at_date:desc'];
    case 'tas-event-transitions':
      return ['tas_event_transition_event_timestamp_date:desc'];
    case 'tas-audit-queues':
      return ['decided_at_date:desc'];
    case 'tas-audit-logs':
      return ['audited_at_date:desc'];
    case 'companies':
      return ['company_name_keyword:asc'];
    case 'loan-account-balance-snapshots':
      return ['snapshot_date_date:desc'];
    case 'recommendations':
      return ['recommendation_started_on_date:desc'];
    case 'pslf-forms':
    case 'tas-program-templates':
    case 'syf-match-participants':
    case 'slr-contribution-plan-participants':
    case 'syf-match-payments':
    case 'syf-match-participant-benefit-years':
    case 'people':
    default:
      // _score:desc is universally safe; _id tiebreaker is added by setSort()
      return ['_score:desc'];
  }
}

/**
 * Get essential fields that must always be included in query results for a dataset.
 * These are needed for routing, linking, and conditional logic in the table.
 */
export function getEssentialFields(dataset: string): string[] {
  switch (dataset) {
    case 'slr-transactions':
      return [
        'id',
        'slr_transaction_id_keyword',
        'slr_transaction_company_id_keyword',
        'slr_transaction_employee_id_keyword',
        'slr_transaction_person_id_keyword',
        'slr_transaction_transaction_date_date',
        'slr_transaction_amount_integer',
        'slr_transaction_state_keyword',
      ];
    case 'tas-program-instances':
      return [
        'tas_application_id_keyword',
        'tas_program_instance_id_keyword',
        'tas_program_template_id_keyword',
        'id',
        'tas_application_state_keyword',
        'tas_program_instance_state_keyword',
        'tas_application_created_at_date',
        'tas_program_instance_created_at_date',
        'company_id_keyword',
        'company_name_keyword',
      ];
    case 'tas-event-transitions':
      return [
        'id',
        'tas_event_transition_id_keyword',
        'tas_event_transition_application_id_keyword',
        'tas_event_transition_program_instance_id_keyword',
        'tas_event_transition_event_domain_keyword',
        'tas_event_transition_event_name_keyword',
        'tas_event_transition_event_timestamp_date',
        'company_id_keyword',
        'tas_event_transition_company_name_keyword',
      ];
    case 'pslf-forms':
      return [
        'id',
        'pslf_form_id_keyword',
        'pslf_form_status_keyword',
        'pslf_form_type_keyword',
        'pslf_form_company_id_keyword',
        'pslf_form_created_at_date',
        'pslf_form_person_id_keyword',
        'pslf_form_person_name_keyword_analyzable',
      ];
    case 'people':
      return ['id', 'person_id_keyword', 'person_first_name_text', 'person_last_name_text'];
    case 'tas-audit-queues':
      return [
        'id',
        'tas_application_id_keyword',
        'company_id_keyword',
        'audit_status_keyword',
        'decided_at_date',
      ];
    case 'tas-audit-logs':
      return [
        'id',
        'audit_queue_id_keyword',
        'tas_application_id_keyword',
        'company_id_keyword',
        'audited_at_date',
      ];
    case 'companies':
      return ['id', 'company_id_keyword', 'company_name_keyword', 'company_type_keyword'];
    case 'tas-program-templates':
      return [
        'id',
        'tas_program_template_company_id_keyword',
        'tas_program_template_company_name_keyword',
        'tas_program_template_created_at_date',
      ];
    case 'syf-match-participants':
      return [
        'id',
        'syf_match_participant_company_id_keyword',
        'syf_match_participant_enrollment_state_keyword',
        'syf_match_participant_created_at_date',
      ];
    case 'syf-match-payments':
      return [
        'id',
        'syf_match_payment_created_at_date',
        'syf_match_participant_company_id_keyword',
        'syf_match_participant_employee_id_keyword',
      ];
    case 'syf-match-participant-benefit-years':
      return [
        'id',
        'syf_match_participant_benefit_year_created_at_date',
        'syf_match_participant_benefit_year_company_id_keyword',
        'syf_match_participant_benefit_year_employee_id_keyword',
      ];
    case 'slr-contribution-plan-participants':
      return [
        'id',
        'slr_contribution_plan_participant_company_id_keyword',
        'slr_contribution_plan_participant_enrollment_status_keyword',
        'slr_contribution_plan_participant_created_at_date',
        'slr_contribution_plan_participant_updated_at_date',
      ];
    case 'loan-account-balance-snapshots':
      return [
        'id',
        'observable_entity_type_keyword',
        'observable_entity_id_keyword',
        'person_id_keyword',
        'company_id_keyword',
        'company_name_keyword',
        'snapshot_date_date',
        'is_latest_boolean',
      ];
    case 'recommendations':
      // Recommendation is a field block on the Person index (RFC 057), so
      // person/company identity fields come from the host Person doc.
      return [
        'id',
        'recommendation_id_keyword',
        'person_id_keyword',
        'user_id_keyword',
        'all_company_ids_keyword',
        'all_company_names_keyword',
        'recommendation_started_on_date',
        'recommendation_completed_on_date',
        'recommendation_completed_boolean',
      ];
    default:
      return ['id', 'created_at'];
  }
}

/**
 * Get default columns to display for each dataset when the user hasn't selected any.
 * Returns snake_case field names matching OpenSearch.  An empty array means
 * "show all columns" (fall through to the existing first-item-key logic).
 *
 * TAS default columns are defined separately in tas-column-config.ts
 * (DEFAULT_COLUMNS.individual / DEFAULT_COLUMNS.aggregate).
 * The entry here is only used for individual/document mode defaults.
 */
export function getDefaultColumns(dataset: string): string[] {
  switch (dataset) {
    case 'slr-transactions':
      // amount is cents (FE /100 via currency_cents?). Both date axes are shown:
      // transaction_date is when it paid, effective_period the month it applies to.
      return [
        'slr_transaction_employee_name_keyword',
        'slr_transaction_company_name_keyword',
        'slr_transaction_transaction_date_date',
        'slr_transaction_effective_period_keyword',
        'slr_transaction_amount_integer',
        'slr_transaction_state_keyword',
        'slr_transaction_line_item_type_keyword',
        'slr_transaction_plan_name_keyword',
        'slr_transaction_has_error_boolean',
      ];
    case 'loan-account-balance-snapshots':
      // balance_amount_integer is cents (FE /100 via currency_cents?); aggregation requires a single observable_entity_type + is_latest_boolean=true.
      return [
        'borrower_name_keyword',
        'company_name_keyword',
        'observable_entity_type_keyword',
        'balance_amount_integer',
        'forgiveness_program_keyword',
        'repayment_plan_keyword',
        'servicer_name_keyword',
        'snapshot_date_date',
      ];
    case 'tas-program-instances':
      // Individual mode defaults — matches DEFAULT_COLUMNS.individual in tas-column-config.ts.
      // Aggregate mode bypasses this entirely (see selectedColumnsArray in programs-page).
      return [
        'employee_name_keyword',
        'employee_payroll_id_keyword',
        'tas_program_template_field_program_name_keyword',
        'tas_program_instance_field_school_institution_name_keyword',
        'tas_application_id_keyword',
        'tas_program_instance_created_at_date',
      ];
    case 'companies':
      return [
        'company_name_keyword',
        'company_type_keyword',
        'company_is_active_boolean',
        'company_is_live_boolean',
        'company_products_enabled_keyword',
        'company_sub_domain_keyword',
        'company_launch_date_date',
        'company_partner_type_keyword',
        'company_created_at_date',
      ];
    case 'people':
      return [
        'person_first_name_text',
        'person_last_name_text',
        'all_employee_names_text',
        'all_emails',
        'all_company_names_keyword',
        'person_is_active_boolean',
        'person_is_enrolled_boolean',
        'all_employee_payroll_ids_text',
      ];
    case 'tas-event-transitions':
      return [
        'tas_event_transition_event_timestamp_pt_date',
        'tas_event_transition_actor_name_keyword',
        'tas_event_transition_actor_category_keyword',
        'tas_event_transition_decision_keyword',
        'tas_event_transition_event_name_keyword',
        'tas_event_transition_application_id_keyword',
        'tas_event_transition_employee_name_keyword',
        'tas_event_transition_company_name_keyword',
        'tas_event_transition_reason_amount_integer',
        'tas_event_transition_reason_note_keyword',
      ];
    case 'pslf-forms':
      return [
        'pslf_form_borrower_name_keyword_analyzable',
        'pslf_form_employer_name_keyword_analyzable',
        'pslf_form_type_keyword',
        'pslf_form_status_keyword',
        'pslf_form_created_at_date',
        'pslf_form_updated_at_date',
      ];
    case 'tas-audit-queues':
      return [
        'applicant_name_keyword',
        'company_name_display_keyword',
        'program_name_display_keyword',
        'action_taken_keyword',
        'decided_by_keyword',
        'audit_reason_keyword',
        'audit_status_keyword',
        'audit_outcome_keyword',
        'requested_amount_cents_integer',
        'decided_at_date',
      ];
    case 'tas-audit-logs':
      return [
        'company_name_display_keyword',
        'program_name_display_keyword',
        'audited_group_keyword',
        'audit_reason_keyword',
        'decision_correct_boolean',
        'correction_applied_boolean',
        'approved_by_group_cents_integer',
        'wrongly_approved_cents_integer',
        'wrongly_denied_cents_integer',
        'audited_at_date',
      ];
    case 'tas-program-templates':
      return [
        'tas_program_template_name_text',
        'tas_program_template_type_keyword_analyzable',
        'tas_program_template_company_name_keyword',
        'tas_program_template_state_keyword_analyzable',
        'tas_program_template_created_at_date',
      ];
    case 'syf-match-participants':
      return [
        'syf_match_participant_person_name_text',
        'syf_match_participant_company_name_keyword',
        'syf_match_participant_enrollment_state_keyword',
        'syf_match_participant_plan_name_text',
        'syf_match_participant_created_at_date',
      ];
    case 'syf-match-payments':
      return [
        'syf_match_payment_transaction_date_date',
        'syf_match_participant_person_name_text',
        'syf_match_participant_company_name_keyword',
        'syf_match_participant_plan_code_keyword',
        'syf_match_participant_benefit_year_integer',
        'syf_match_payment_transaction_amount_integer',
        'syf_match_payment_payment_qualified_status_keyword',
        'syf_match_payment_payment_certification_status_keyword',
      ];
    case 'syf-match-participant-benefit-years':
      return [
        'syf_match_participant_benefit_year_person_name_text',
        'syf_match_participant_benefit_year_company_name_keyword',
        'syf_match_participant_benefit_year_plan_code_keyword',
        'syf_match_participant_benefit_year_integer',
        'syf_match_participant_benefit_year_total_payment_count_integer',
        'syf_match_participant_benefit_year_total_paid_amount_integer',
        'syf_match_participant_benefit_year_completion_bucket_keyword',
      ];
    case 'slr-contribution-plan-participants':
      return [
        'slr_contribution_plan_participant_first_name_text',
        'slr_contribution_plan_participant_last_name_text',
        'slr_contribution_plan_participant_company_name_keyword',
        'slr_contribution_plan_participant_enrollment_status_keyword',
        'slr_contribution_plan_participant_plan_name_text',
        'slr_contribution_plan_participant_created_at_date',
      ];
    case 'recommendations':
      return [
        'all_company_names_keyword',
        'person_id_keyword',
        'recommendation_projected_savings_on_payoff_amount_integer',
        'recommendation_projected_savings_on_total_interest_paid_amount_integer',
        'recommendation_projected_savings_on_monthly_payment_amount_integer',
      ];
    default:
      // Empty = show all columns (existing behavior)
      return [];
  }
}
